/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Dmitriy Kvon
 * @author Andrey Dubov
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.20.1
 */
import { useEffect, useMemo } from 'react';

import { useFormikContext } from 'formik';
import map from 'lodash/map';
import { useSelector } from 'react-redux';

import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';

import {
    ECreateInquiryFieldType,
    ECreateInquiryFieldVisibility as Visibility,
    setInitialValidationScheme,
} from '../../constants';
import { useMasterAttributes } from '../../hooks/useMasterAttributes';
import { inquiryApi } from '../../inquiryApi';
import { mapCustomAttributeValues } from '../../mapping';
import {
    ICreateInquiryAttribute,
    ICreateInquiryChangeAttributeQuery,
    ICreateInquiryForm,
    ICreateInquiryGetAttributes,
    ICreateInquiryInitialise,
    TCreateInquiryAttributeValue,
    TCreateInquiryFormValidation,
} from '../../types';
import { setInquiryValidationScheme } from '../../util/validation';
import { InquiryAttributesStyled } from '../styled/CreateInquiryAtrributeStyled';
import CreateInquiryCustomAttribute from './CreateInquiryCustomAttribute';

interface ICreateInquireInitialAttributes extends ICreateInquiryInitialise {
    topicCode: string;

    attributes?: ICreateInquiryGetAttributes;

    setValidation: (validation: TCreateInquiryFormValidation) => void;
    onChangeAttribute: (
        topicCode: string,
        attributeId: number,
        masterAttributes: ICreateInquiryChangeAttributeQuery['masterAttributes'],
        validateForm: () => void,
    ) => void;
}

const CreateInquiryCustomAttributes = ({
    attributes,
    onChangeAttribute,
    setValidation,
    ...params
}: ICreateInquireInitialAttributes) => {
    /* Api Selectors */
    const translationCommon = useSelector(makeSelectTranslationCommon());
    const masterAttributesConfig = useMasterAttributes(attributes);

    const [, { isLoading: isChangingAttribute }] = inquiryApi.endpoints.changeAttribute.useMutation();

    /* Form Context */
    const { values, setFieldValue, getFieldProps, validateForm } = useFormikContext<ICreateInquiryForm>();

    const currentMasterAttributes = useMemo(() => {
        const customAttrsMapper = mapCustomAttributeValues(values);
        return map(masterAttributesConfig, customAttrsMapper)
            .filter(item => item !== null)  as TCreateInquiryAttributeValue[] ;
    }, [masterAttributesConfig, values]);

    // если произошло изменение атрибутов, то апдейтим значения "зависимых" атрибутов
    useEffect(() => {
            if (!!attributes && !isChangingAttribute) {
                attributes.customAttrs?.forEach((attribute: ICreateInquiryAttribute) => {
                    const value = {
                        [ECreateInquiryFieldType.string]: attribute.declaration.stringValue,
                        [ECreateInquiryFieldType.number]: attribute.declaration.numberValue,
                        [ECreateInquiryFieldType.date]: attribute.declaration.dateValue,
                        [ECreateInquiryFieldType.bool]: attribute.declaration.booleanValue,
                        [ECreateInquiryFieldType.dictionary]: attribute.declaration.multiSelect
                            ? attribute.declaration.values
                                ?.map((value) => ({ value: value.id, label: value.name }))
                            : attribute.declaration.values
                                ?.map((value) => ({ value: value.id, label: value.name }))?.[0],
                        [ECreateInquiryFieldType.dbQuery]: attribute.declaration.values,
                    }[attribute.declaration.type];

                    if (attribute.declaration.isSlave && !attribute.declaration.isMaster) {
                        setFieldValue(attribute.code, value, false);
                    }
                });
            }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attributes, isChangingAttribute]);

    /* Validation schema */
    useEffect(() => {
        if (!attributes) return;

        const attributeValidationScheme = setInquiryValidationScheme(
            translationCommon.form.validation,
            attributes.customAttrs,
            attributes.serviceAttributes,
        );

        setValidation({
            ...setInitialValidationScheme(translationCommon.form.validation),
            ...attributeValidationScheme,
        });

        attributes.customAttrs?.forEach((attribute: ICreateInquiryAttribute) => {
            const value = {
                [ECreateInquiryFieldType.string]: attribute.declaration.stringValue,
                [ECreateInquiryFieldType.number]: attribute.declaration.numberValue,
                [ECreateInquiryFieldType.date]: attribute.declaration.dateValue,
                [ECreateInquiryFieldType.bool]: attribute.declaration.booleanValue,
                [ECreateInquiryFieldType.dictionary]: attribute.declaration.multiSelect
                    ? attribute.declaration.values?.map((value) => ({ value: value.id, label: value.name }))
                    : attribute.declaration.values?.map((value) => ({ value: value.id, label: value.name }))?.[0],
                [ECreateInquiryFieldType.dbQuery]: attribute.declaration.values,
            }[attribute.declaration.type];

            if (!getFieldProps(attribute.code).value) {
                setFieldValue(attribute.code, value, false);
            }
        });
    }, [attributes, getFieldProps, setFieldValue, setValidation, translationCommon.form.validation]);

    /* Handlers */

    const onAttributeChange = (attribute: ICreateInquiryAttribute, newValue: any) => {
        const formValues = { ...values, [attribute.code]: newValue };

        // Map form custom attributes values to api configuration
        const customAttrsMapper = mapCustomAttributeValues(formValues);

        const masterAttributes: TCreateInquiryAttributeValue[] =
            map(masterAttributesConfig, customAttrsMapper)
            .filter(item => item !== null) as TCreateInquiryAttributeValue[];

        onChangeAttribute(params.topicCode, attribute.id, masterAttributes, validateForm);
    };

    if (!attributes) return null;

    return (
        <InquiryAttributesStyled>
            {attributes.customAttrs
                .filter(({ declaration }: ICreateInquiryAttribute) => declaration.accessibility !== Visibility.hidden)
                .map((attribute: ICreateInquiryAttribute) => {
                    const dictionaryOptions =
                        attribute.dictionaryCode && attributes?.dictionaries?.[attribute.dictionaryCode]
                            ? attributes.dictionaries[attribute.dictionaryCode]?.values?.map((dictionary) => ({
                                  value: dictionary.id,
                                  label: dictionary.name,
                              })) ?? []
                            : [];

                    return (
                        <CreateInquiryCustomAttribute
                            attribute={attribute}
                            dictionary={dictionaryOptions}
                            id={params.id}
                            key={attribute.id}
                            masterAttributes={currentMasterAttributes}
                            topicCode={params.topicCode}
                            type={params.type}
                            onAttributeChange={onAttributeChange}
                        />
                    );
                })}
        </InquiryAttributesStyled>
    );
};

export default CreateInquiryCustomAttributes;
